import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import useWebSocket from 'react-use-websocket';
import { Alert, Flex, Progress, Typography } from 'antd';

import useLayoutStore from 'hooks/stores/useLayoutStore';
import useSessionStore from 'hooks/stores/useSessionStore';

const WEB_SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_URL || 'ws://localhost:8080';

const RealTimeAssetsProcessing: FC = () => {
  const sessionId = useSessionStore((state) => state.sessionId);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [assetName, setAssetName] = useState('');
  const [processedPages, setProcessedPages] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [status, setStatus] = useState('');
  const [visible, setVisible] = useLayoutStore((state) => [
    state.shouldDisplayAssetsProcessing,
    state.setShouldDisplayAssetsProcessing,
  ]);

  // Alleen een geldige WebSocket URL maken als sessionId bestaat
  const socketUrl = sessionId ? `${WEB_SOCKET_URL}/${sessionId}/assets/status` : null;

  console.log('[DEBUG] WebSocket URL for assets status:', socketUrl);

  const { readyState, lastMessage } = useWebSocket(socketUrl, {
    shouldReconnect: () => !!sessionId,
    reconnectInterval: 3000,
    share: true,
    onOpen: () => {
      console.log('[DEBUG] WebSocket connection opened for assets status');
    },
    onClose: () => {
      console.log('[DEBUG] WebSocket connection closed for assets status');
    },
    onError: (event) => {
      console.error('[DEBUG] WebSocket error for assets status:', event);
      //setError('WebSocket connection error');
    },
    // Alleen verbinden als we een geldig socketUrl hebben
    reconnectAttempts: socketUrl ? 10 : 0,
  });

  const isConnected = readyState === 1;
  const isVisible = isConnected && visible;

  const hasProcessedPages = processedPages > 0;

  useEffect(() => {
    if (lastMessage) {
      try {
        console.log('[DEBUG] Assets status websocket message received:', lastMessage.data);
        const { data } = lastMessage;
        const parsedData = JSON.parse(data);
        console.log('[DEBUG] Parsed assets status data:', parsedData);

        const { percentage, status, error, assetName, numberOfProcessedPages, pageCount } =
          parsedData as {
            percentage: number;
            status: string;
            assetName: string;
            numberOfProcessedPages?: number;
            pageCount?: number;
            error?: string;
          };

        setStatus(status);
        setProcessedPages(numberOfProcessedPages || 0);
        setPageCount(pageCount || 0);
        setAssetName(assetName);
        setVisible(true);

        if (status === 'error') {
          console.error(`[DEBUG] Asset processing error:`, error);
          // Use generic error for common error messages
          const genericErrors = [
            'Processing failed',
            'Invalid or corrupted file',
            'Failed to process file',
            'Processing timeout - file may be too large or corrupted',
          ];
          const isGenericError = !error || genericErrors.includes(error);
          const errorMessage = isGenericError
            ? (t('RealTimeProcessing.GenericError') || 'Failed to process file')
            : error;
          setError(errorMessage);
        } else {
          setError(''); // Wis evt. bestaande errors als status niet 'error' is
        }

        setProgress(percentage);
      } catch (err) {
        console.error('[DEBUG] Error parsing assets status message:', err);
        setError('Error parsing websocket message');
      }
    }
  }, [lastMessage, setVisible]);

  useEffect(() => {
    const hasProcessed = progress === 100;
    if (!hasProcessed) return;

    console.log(
      '[DEBUG] Asset processing completed (progress 100%), refreshing assets and products',
    );
    const timeOut = setTimeout(() => {
      queryClient.invalidateQueries('assets');
      queryClient.invalidateQueries('products');
      setProgress(0);
      setVisible(false);
    }, 2500);

    return () => {
      clearTimeout(timeOut);
    };
  }, [progress, setVisible, queryClient]);

  // reset error after 5 seconds
  useEffect(() => {
    if (!error) return;

    const timeOut = setTimeout(() => {
      console.log('[DEBUG] Clearing error after timeout');
      setError('');
      setVisible(false);
    }, 5000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [error, setVisible]);

  // Als er nog geen websocket verbinding is of geen zichtbare status, toon niets
  if (!isVisible || !lastMessage) return null;

  if (error) return <Alert message={error} type="error" showIcon closable />;

  return (
    <Flex vertical align="center" justify="center">
      <Progress percent={progress} strokeColor={{ from: '#108ee9', to: '#87d068' }} />
      <h5 style={{ textAlign: 'center', marginBottom: 0 }}>
        {t(`RealTimeProcessing.Processing_${status}`, { assetName, error })}
      </h5>
      {hasProcessedPages ? (
        <h5 style={{ textAlign: 'center', marginBottom: 0 }}>
          {t('RealTimeProcessing.Processed', { pages: processedPages, pageCount: pageCount })}
        </h5>
      ) : (
        ''
      )}
      <Typography.Text type="secondary" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        {t('RealTimeProcessing.Instruction')}
      </Typography.Text>
    </Flex>
  );
};

export default RealTimeAssetsProcessing;
